'use client';

import { Button, Dropdown, ListBox, Select, toast } from '@heroui/react';
import { PromptInput, type ChatStatus } from '@heroui-pro/react/prompt-input';
import {
  IconArrowNarrowUp,
  IconCheck,
  IconChevronDown,
  IconPaperclip,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AssetSelectorModal,
  type SelectedAsset,
} from '@/components/asset-selector';
import {
  AI_CHAT_DEFAULT_MODEL_API,
  AI_CHAT_PANEL_MODE_OPTIONS,
  AI_IMAGE_COUNT_OPTIONS,
} from '../constants';
import { useAiModels } from '../hooks/use-ai-models';
import type { AiChatPanelMode, AiChatUiAttachment, AiImageSettings } from '../types';
import { formatImageSize, getBaseSize } from '../utils/image-size';
import { AiChatAttachmentList } from './AiChatAttachmentList';
import { AiImageSettingsPopover } from './AiImageSettingsPopover';

function revokeAttachmentUrl(attachment: AiChatUiAttachment) {
  if (attachment.src?.startsWith('blob:')) URL.revokeObjectURL(attachment.src);
}

const DEFAULT_IMAGE_SETTINGS: AiImageSettings = {
  aspectRatio: '21:9',
  resolution: '2k',
  ...getBaseSize('21:9', '2k'),
  linked: true,
  count: 2,
};

export type AiChatComposerSubmitPayload =
  | {
      mode: 'CHAT';
      text: string;
      modelApiId: string;
      attachments: AiChatUiAttachment[];
    }
  | {
      mode: 'IMAGE';
      text: string;
      modelApiId: string;
      attachments: AiChatUiAttachment[];
      imageSettings: AiImageSettings;
      size: string;
    };

export type AiChatComposerProps = {
  className?: string;
  mode: AiChatPanelMode;
  onModeChange: (mode: AiChatPanelMode) => void;
  placeholder?: string;
  disabled?: boolean;
  onSubmit: (payload: AiChatComposerSubmitPayload) => void | Promise<void>;
  onStop?: () => void;
  status?: ChatStatus;
};

export function AiChatComposer({
  className,
  mode,
  onModeChange,
  placeholder = '向AI发送消息',
  disabled,
  onSubmit,
  onStop,
  status: externalStatus,
}: AiChatComposerProps) {
  const [value, setValue] = useState('');
  const [attachments, setAttachments] = useState<AiChatUiAttachment[]>([]);
  const [internalStatus, setInternalStatus] = useState<ChatStatus>('ready');
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const [imageSettings, setImageSettings] = useState<AiImageSettings>(DEFAULT_IMAGE_SETTINGS);
  const [assetSelectorOpen, setAssetSelectorOpen] = useState(false);
  const attachmentsRef = useRef(attachments);

  const status = externalStatus ?? internalStatus;
  const { models, isLoading: modelsLoading } = useAiModels(mode);

  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  useEffect(() => {
    return () => {
      attachmentsRef.current.forEach(revokeAttachmentUrl);
    };
  }, []);

  useEffect(() => {
    if (!models.length) {
      setSelectedModelId(null);
      return;
    }
    if (!selectedModelId || !models.some((m) => m.id === selectedModelId)) {
      setSelectedModelId(models[0]!.id);
    }
  }, [models, selectedModelId]);

  const selectedModel = useMemo(
    () => models.find((m) => m.id === selectedModelId) ?? models[0],
    [models, selectedModelId]
  );

  const modelApiId = selectedModel?.apiModelId || AI_CHAT_DEFAULT_MODEL_API;
  const isGenerating = status === 'submitted' || status === 'streaming';
  const canSend = Boolean(value.trim() || attachments.length) && !disabled;

  const modeLabel =
    AI_CHAT_PANEL_MODE_OPTIONS.find((item) => item.id === mode)?.label ?? 'AI会话';

  const imageSummary = `${imageSettings.aspectRatio} | ${imageSettings.resolution === '2k' ? '2K' : '4K'}`;

  const handleModeSelect = (next: AiChatPanelMode) => {
    const option = AI_CHAT_PANEL_MODE_OPTIONS.find((item) => item.id === next);
    if (!option?.enabled) {
      toast.info('该能力即将上线');
      return;
    }
    onModeChange(next);
  };

  const handleSubmit = async () => {
    const trimmed = value.trim();
    if (!canSend || isGenerating) return;
    if (!selectedModel && mode !== 'CHAT') {
      toast.danger('请选择模型');
      return;
    }

    const payloadAttachments = [...attachments];
    setValue('');
    setAttachments([]);
    if (!externalStatus) setInternalStatus('submitted');

    try {
      if (mode === 'IMAGE') {
        await onSubmit({
          mode: 'IMAGE',
          text: trimmed,
          modelApiId,
          attachments: payloadAttachments,
          imageSettings,
          size: formatImageSize(imageSettings.width, imageSettings.height),
        });
      } else {
        await onSubmit({
          mode: 'CHAT',
          text: trimmed,
          modelApiId,
          attachments: payloadAttachments,
        });
      }
    } finally {
      if (!externalStatus) setInternalStatus('ready');
    }
  };

  const handleAssetsSelected = useCallback((assets: SelectedAsset[]) => {
    setAttachments((current) => {
      const existingIds = new Set(current.map((item) => item.assetId ?? item.id));
      const next = [...current];
      for (const asset of assets) {
        if (existingIds.has(asset.id)) continue;
        existingIds.add(asset.id);
        next.push({
          id: asset.id,
          assetId: asset.id,
          name: asset.name?.trim() || '未命名',
          mimeType: asset.mimeType ?? undefined,
          src: (asset.url ?? '').trim() || undefined,
        });
      }
      return next;
    });
  }, []);

  const handleRemoveAttachment = (attachment: { id?: string }) => {
    if (!attachment.id) return;
    setAttachments((current) => {
      const removed = current.find((item) => item.id === attachment.id);
      if (removed) revokeAttachmentUrl(removed);
      return current.filter((item) => item.id !== attachment.id);
    });
  };

  const handleStop = useCallback(() => {
    onStop?.();
    if (!externalStatus) setInternalStatus('ready');
  }, [externalStatus, onStop]);

  return (
    <>
      <PromptInput
        className={className}
        status={status}
        value={value}
        variant="primary"
        onStop={handleStop}
        onSubmit={() => void handleSubmit()}
        onValueChange={setValue}
      >
      <PromptInput.Shell>
        <PromptInput.Content>
          {attachments.length ? (
            <PromptInput.Attachments>
              <AiChatAttachmentList attachments={attachments} onRemove={handleRemoveAttachment} />
            </PromptInput.Attachments>
          ) : null}
          <PromptInput.TextArea placeholder={placeholder} />
        </PromptInput.Content>
        <PromptInput.Toolbar>
          <PromptInput.ToolbarStart className="flex flex-wrap items-center gap-2">
            <PromptInput.Action
              aria-label="添加附件"
              isDisabled={isGenerating || disabled}
              tooltip="添加附件"
              onPress={() => setAssetSelectorOpen(true)}
            >
              <IconPaperclip className="size-4" stroke={1.75} />
            </PromptInput.Action>

            <Dropdown>
              <Dropdown.Trigger>
                <Button
                  isDisabled={isGenerating || disabled}
                  size="sm"
                  variant="secondary"
                >
                  {modeLabel}
                  <IconChevronDown className="size-3.5 opacity-60" stroke={1.75} />
                </Button>
              </Dropdown.Trigger>
              <Dropdown.Popover className="min-w-40">
                <Dropdown.Menu
                  onAction={(key) => handleModeSelect(String(key) as AiChatPanelMode)}
                >
                  {AI_CHAT_PANEL_MODE_OPTIONS.map((item) => (
                    <Dropdown.Item key={item.id} id={item.id} textValue={item.label}>
                      <span className="flex w-full items-center justify-between gap-2">
                        <span>{item.label}</span>
                        {mode === item.id ? (
                          <IconCheck className="size-4" stroke={2} />
                        ) : null}
                      </span>
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown>

            <Select
              aria-label="选择模型"
              className="w-56"
              isDisabled={isGenerating || disabled || modelsLoading}
              value={selectedModel?.id ?? null}
              onChange={(key) => setSelectedModelId(key != null ? String(key) : null)}
            >
              <Select.Trigger>
                <Select.Value>{selectedModel?.label ?? '选择模型'}</Select.Value>
                <Select.Indicator />
              </Select.Trigger>
              <Select.Popover>
                <div className="text-muted px-3 pb-1 pt-2 text-xs">选择模型</div>
                <ListBox>
                  {models.map((model) => (
                    <ListBox.Item key={model.id} id={model.id} textValue={model.label}>
                      {model.label}
                      <ListBox.ItemIndicator />
                    </ListBox.Item>
                  ))}
                </ListBox>
              </Select.Popover>
            </Select>

            {mode === 'IMAGE' ? (
              <>
                <AiImageSettingsPopover
                  disabled={isGenerating || disabled}
                  settings={imageSettings}
                  summary={imageSummary}
                  onChange={setImageSettings}
                />
                <Select
                  aria-label="生成张数"
                  isDisabled={isGenerating || disabled}
                  value={String(imageSettings.count)}
                  className="w-20"
                  onChange={(key) =>
                    setImageSettings((prev) => ({
                      ...prev,
                      count: Number(key),
                    }))
                  }
                >
                  <Select.Trigger>
                    <Select.Value />
                    <Select.Indicator />
                  </Select.Trigger>
                  <Select.Popover>
                    <ListBox>
                      {AI_IMAGE_COUNT_OPTIONS.map((count) => (
                        <ListBox.Item
                          key={count}
                          id={String(count)}
                          textValue={`${count}张`}
                        >
                          {count}张
                          <ListBox.ItemIndicator />
                        </ListBox.Item>
                      ))}
                    </ListBox>
                  </Select.Popover>
                </Select>
              </>
            ) : null}
          </PromptInput.ToolbarStart>

          <PromptInput.ToolbarEnd>
            <Button
              isIconOnly
              aria-label={isGenerating ? '停止' : '发送'}
              isDisabled={!canSend && !isGenerating}
              onPress={() => (isGenerating ? handleStop() : void handleSubmit())}
            >
              <IconArrowNarrowUp className="size-5" stroke={2} />
            </Button>
          </PromptInput.ToolbarEnd>
        </PromptInput.Toolbar>
      </PromptInput.Shell>
      </PromptInput>

      <AssetSelectorModal
        open={assetSelectorOpen}
        title="添加参考附件"
        onOpenChange={setAssetSelectorOpen}
        onConfirm={handleAssetsSelected}
      />
    </>
  );
}
